import { graphql, navigate } from 'gatsby';
import React, { useEffect } from 'react';
import {
  getImageFieldStyle,
  getSettingsClass,
  preloadImage,
} from '~/utils/helpers';
import dynamicFormConfig from '~/data/config/dynamic-forms.json';
import {
  DynamicFormField,
  Submit,
  TermsAndConditions,
} from './DynamicFormFields';
import countries from '../../../data/sidebar/countries.json';
import { useForm, type FieldErrors } from 'react-hook-form';
import type { FormValues } from './validators';
import type {
  DynamicFormValues,
  FieldProps,
  FormField,
} from '../../../types/form';
import { DynamicFormImage } from '.';

interface DynamicFormProps {
  block: DynamicFormValues;
}

type ConfigFormField = FieldProps & FormField;

function getNewFormStyle(form_style?: string) {
  if (form_style === 'left') {
    return { alignment: 'justify-start', size: 'w-3/4 h-fit' };
  } else if (form_style === 'right') {
    return { alignment: 'justify-end', size: 'w-3/4 h-fit' };
  }
  return { alignment: 'justify-center', size: 'w-full h-fit' };
}

export const DynamicForm = ({ block }: DynamicFormProps) => {
  const settingsClass = getSettingsClass(block.settings);
  const formId = block.form_title.toLowerCase().replace(/ /g, '-');
  const methods = useForm<FormValues>();
  const {
    handleSubmit,
    setError,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = methods;
  const { alignment, size } = getNewFormStyle(block.form_style);
  const columnCount = block.form_column_count ?? 1;
  const colsClass = `grid-cols-${block.form_column_count ?? 1}`;
  const spanClass = `col-span-${columnCount}`;
  const formFields = block.form_fields.reduce((acc, field) => {
    const configFormField = dynamicFormConfig.reusable_fields.find(
      f => f.field_key === field._template
    );
    if (configFormField) {
      acc.push({
        ...configFormField,
        column_span: field.column_span,
        _template: field._template,
      });
    }

    return acc;
  }, [] as ConfigFormField[]);

  const onSubmit = async (data: FormValues) => {
    try {
      const formBody = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch(dynamicFormConfig.api_url, {
        method: 'POST',
        body: formBody,
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error();
      }

      navigate(block.form_redirect_url);
    } catch (error: any) {
      setError('root', {
        type: 'server',
        message:
          "Oops, our bunnies couldn't send your message. Please try again later.",
      });
    }
  };

  useEffect(() => {
    // Preload all country flags
    countries.list.forEach(country => {
      preloadImage(`/flags/${country.sortname}.svg`);
    });
  }, []);

  return (
    <div
      className={`block__dynamic_form flex h-fit w-full justify-center md:p-4`}
    >
      <div
        className={`flex max-w-xl xl:max-w-lg lg:max-w-md ${alignment} md:justify-center`}
      >
        <div className={`relative ${size} md:w-full`}>
          {block.form_image && <DynamicFormImage image={block.form_image} />}
          <form
            action={dynamicFormConfig.api_url}
            method='POST'
            className={`text-inherit shadow-lg relative grid gap-x-4 bg-blue-form p-12 border-rounded-strong md:p-8 ${settingsClass} ${colsClass}`}
            onSubmit={handleSubmit(onSubmit)}
            onChange={() => {
              methods.clearErrors();
            }}
          >
            {formFields?.length > 0 &&
              formFields.map((field, index) => {
                return (
                  <DynamicFormField
                    key={`${field._template}${index}`}
                    field={field}
                    setValue={methods.setValue}
                    showError={isNextOrOnlyError(field, errors)}
                    register={methods.register}
                    formId={formId}
                    columnSpan={field.column_span ?? 1}
                    columnCount={columnCount}
                  />
                );
              })}
            <div
              className={`form-submit flex w-full flex-col items-center gap-8 ${spanClass}`}
            >
              <TermsAndConditions
                register={methods.register}
                formId={formId}
              />
              <Submit
                label={block.form_submit_label}
                pending={isSubmitting}
                submitted={isSubmitSuccessful}
              />
              <input
                type='hidden'
                name='valid'
                value='false'
              />
              <Errors data={errors} />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

/**
 * DynamicFormReusable
 * Similar to DynamicForm, but uses a reusable form configuration.
 * Useful for when you want to reuse a form configuration across multiple pages.
 */
export const DynamicFormReusable = ({
  block,
}: {
  block: {
    reusable_form_id: string;
    settings: Settings;
    form_style: string;
  };
}) => {
  const reusedForm = dynamicFormConfig.reusable_forms.find(
    configForm => configForm.form_id === block.reusable_form_id
  );

  if (!reusedForm) {
    return null;
  }

  return (
    <DynamicForm
      block={{
        ...reusedForm,
        settings: block.settings,
        form_style: block.form_style,
      }}
    />
  );
};

const isNextOrOnlyError = (field: FieldProps, errors: FieldErrors) => {
  if (Object.keys(errors).length === 1) {
    return true;
  }

  const fieldError = errors[field.field_key];

  return fieldError ? true : false;
};

const Errors = ({ data }: { data?: FieldErrors }) => {
  let errorMessage = null;
  if (!data || Object.keys(data).length === 0) {
    return null;
  }

  const errorObject = Object.values(data)[0];
  if (!errorObject) {
    return null;
  }

  if (typeof errorObject.message === 'string') {
    errorMessage = errorObject.message;
  } else if (Array.isArray(errorObject)) {
    errorMessage = errorObject[0].message;
  }

  if (errorMessage) {
    return <div className='text-xs text-invalid-accent'>*{errorMessage}</div>;
  }

  return null;
};

export const dynamicFormReusableFragment = graphql`
  fragment DynamicFormReusableFragment on Block {
    reusable_form_id
    settings {
      ...SettingsFragment
    }
    form_style
  }
`;

export const dynamicFormFragment = graphql`
  fragment DynamicFormFragment on Block {
    settings {
      ...SettingsFragment
    }
    reusable_form_id
    form_column_count
    form_fields {
      column_span
      _template
    }
    form_id
    form_image {
      ...ImageFieldFragment
    }
    form_recipients {
      _template
    }
    form_style
    form_submit_label
    form_title
    form_redirect_url
  }
`;
